import { brandGradientIsReadable, themeVarName } from '@mfw/common';

import { onSurface, onSurfaceMark } from './onSurface';

/**
 * `--mfw-cta-fill` / `--mfw-cta-ink` — the gradient CTA's two variables, and
 * the one part of the effect layer a stylesheet cannot decide for itself.
 *
 * The label sits over BOTH stops of a brand→accent gradient, so it has to clear
 * AA at the WORSE one. Measured across brand hues that fails for mid-lightness
 * brands — magenta with the default azure accent is 2.61:1 — and the answer is
 * not a different angle but to stop being a gradient: a solid `primary-main`
 * fill carries its own derived `contrastText`, which `contrastColorForRgb`
 * bounds at 4.58:1 for any colour.
 *
 * Reader-shaped, exactly like `applyOnSurfaceRamps`, and for the same reason:
 * TWO paths write `:root`. The provider applies a resolved theme record; the
 * rebrand editor previews one var at a time against what is already on the
 * element. A measurement that only one of them performed would let the editor
 * promise a gradient that the save then refuses — the same class of split the
 * tab indicator and its label were in.
 */
type ThemeValueReader = (varName: string) => string | undefined;

/**
 * The CTA's fill for a brand and accent: the brand→accent gradient when one
 * label colour reads on both stops, else the solid brand. Shared by the root
 * writer below and the rebrand editor's preview, so the editor never shows a
 * gradient the app would refuse.
 */
export const ctaFillOf = (primaryMain: string, accentMain: string) => {
  let gradient = false;
  try {
    gradient = brandGradientIsReadable(primaryMain, accentMain);
  } catch {
    // A stored layer — or a half-typed picker — can hold a value the colour
    // parser rejects. Degrade to the solid fill rather than throwing out of the
    // theme apply and leaving the page with no palette at all.
    gradient = false;
  }

  return {
    fill: gradient
      ? `linear-gradient(135deg, ${primaryMain} 0%, ${accentMain} 100%)`
      : primaryMain,
    gradient,
  };
};

export function applyBrandCta(
  root: HTMLElement,
  readThemeValue: ThemeValueReader,
) {
  const primaryMain = readThemeValue(themeVarName.primary.main);
  const accentMain = readThemeValue(themeVarName.accent.main);
  const ink = readThemeValue(themeVarName.primary.contrastText);

  if (!primaryMain || !accentMain) return;

  root.style.setProperty(
    '--mfw-cta-fill',
    ctaFillOf(primaryMain, accentMain).fill,
  );

  // The brand's own contrast text either way: it is what
  // `brandGradientIsReadable` measured the gradient against, and it is what a
  // solid `primary-main` fill wants regardless.
  if (ink) {
    root.style.setProperty('--mfw-cta-ink', ink);
  }
}

/**
 * The gradient as TEXT — an `sx` fragment for a glyph run (the player's rep
 * counter, its timers and its progress percentage) that wants the CTA's look.
 *
 * `--mfw-cta-fill` is made for a button face: its label is the brand's own
 * `contrastText`, measured over both stops. Clipped to the glyphs the gradient
 * IS the text, drawn straight on the page, and on a light page the brand → accent
 * run reads 1.7–2.4:1 (audit L8). There is no `contrastText` to lean on, so a
 * light surface gets the brand's on-surface step instead of the gradient, keyed
 * off the flag the provider writes on `<html>` (`data-mfw-surface`). The dark
 * ladder keeps the gradient, as it has always been drawn there.
 *
 * The selector is `html[…] &`, not `:root[…] &` as the stylesheet spells it:
 * stylis reads a nested key that STARTS with a colon as a pseudo-class of the
 * parent and emits `.css-x:root[…] .css-x`, which matches nothing. Same element,
 * same flag, and a rule that actually applies.
 *
 * Spread it FIRST, then add the call site's own sizing, filter or tabular
 * numerals: the nested rule only needs to out-specify the base, not to come last.
 */
export const brandGradientText = {
  background: 'var(--mfw-cta-fill)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  "html[data-mfw-surface='light'] &": {
    background: 'none',
    color: onSurface.primary,
    WebkitTextFillColor: onSurface.primary,
  },
} as const;

/**
 * The gradient as a BAR FILL — an `sx` fragment for a progress bar (the player
 * header's per-group segments, the rest timer's bar) that wants the CTA's look.
 *
 * A bar is a MARK, not a button face: its job is to stand out from the track it
 * runs along, and the brand → accent run does not on a light page — 1.7–2.4:1 on
 * white and 1.4–2.0:1 on beige against the track (SC 1.4.11 asks 3:1). The
 * percentage beside it carries the same figure as text, but a bar that cannot be
 * told from its track is still a broken bar. A light surface gets the brand's
 * mark step (`onSurfaceMark.primary`, measured at 3:1 on the page and its cards)
 * instead of the gradient; the dark ladder keeps the gradient it has always had.
 *
 * Same flag and same selector form as `brandGradientText` (`html[…] &`, not
 * `:root[…] &` — see there). Spread it FIRST, then add the call site's own glow,
 * transition and width.
 */
export const brandGradientFill = {
  background: 'var(--mfw-cta-fill)',
  "html[data-mfw-surface='light'] &": {
    background: onSurfaceMark.primary,
  },
} as const;
